import React, { useEffect, useState, useMemo, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import SystemMessageCard from './SystemMessageCard';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fetchUnreadSystemMessages } from '@/utils/system-messages-unread';


interface SystemMessage {
  id: string;
  title: string;
  body: string;
  created_at: string;
  target_type: 'all' | 'all_clients' | 'all_practitioners' | 'specific_user';
}

interface SystemMessageExtended extends SystemMessage {
  target_user_id?: string | null;
  expires_at?: string | null;
}

interface SystemMessagesListProps {
  userType: 'client' | 'practitioner';
  className?: string;
  onUnreadCountChange?: (count: number) => void;
}

const logSystemMessageDecision = (
  phase: string,
  messageId: string,
  readIds: Set<string>,
  dismissedIds: Set<string>,
  skipped: boolean,
  reason: string,
) => {
  console.log('[SystemMessagesList] message decision', {
    phase,
    messageId,
    readIds: Array.from(readIds),
    dismissedIds: Array.from(dismissedIds),
    skipped,
    action: skipped ? 'skipped' : 'inserted',
    reason,
  });
};

const SystemMessagesList: React.FC<SystemMessagesListProps> = ({
  userType,
  className = "",
  onUnreadCountChange
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [messages, setMessages] = useState<SystemMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [readMessages, setReadMessages] = useState<string[]>([]);

  // Refs so realtime handlers always see latest archived sets without resubscribing
  const readIdsRef = useRef<Set<string>>(new Set());
  const dismissedIdsRef = useRef<Set<string>>(new Set());

  // Calculate unread count
  const unreadCount = useMemo(() => {
    return messages.filter(msg => !readMessages.includes(msg.id)).length;
  }, [messages, readMessages]);

  // Notify parent component when unread count changes
  useEffect(() => {
    if (onUnreadCountChange) {
      onUnreadCountChange(unreadCount);
    }
  }, [unreadCount, onUnreadCountChange]);

  const handleMarkAsRead = (messageId: string) => {
    readIdsRef.current.add(messageId);
    setReadMessages(prev => (prev.includes(messageId) ? prev : [...prev, messageId]));
    // Hide from active dashboard list once read (history page will still show it)
    setMessages(current => current.filter(m => m.id !== messageId));
  };

  const handleDismiss = (messageId: string) => {
    dismissedIdsRef.current.add(messageId);
    setMessages(current => current.filter(m => m.id !== messageId));
  };


  useEffect(() => {
    const fetchMessages = async () => {
      if (!user) return;

      try {
        setIsLoading(true);
        const unreadResult = await fetchUnreadSystemMessages({
          userId: user.id,
          userEmail: user.email,
          userType,
        });

        const dismissedIds = unreadResult.dismissedIds;
        const readIds = unreadResult.readIds;
        dismissedIdsRef.current = dismissedIds as Set<string>;
        readIdsRef.current = readIds as Set<string>;
        setReadMessages(Array.from(readIds) as string[]);

        const archivedIds = Array.from(new Set([...Array.from(readIds), ...Array.from(dismissedIds)])) as string[];
        archivedIds.forEach(messageId => {
          logSystemMessageDecision(
            'refetch-prequery-exclusion',
            messageId,
            readIds as Set<string>,
            dismissedIds as Set<string>,
            true,
            readIds.has(messageId) ? 'read' : 'dismissed',
          );
        });

        console.log('[SystemMessagesList] shared unread-count source', unreadResult.debug);

        const visibleMessages = unreadResult.unreadMessages.filter(msg => {
          logSystemMessageDecision(
            'refetch-query-result',
            msg.id,
            readIds as Set<string>,
            dismissedIds as Set<string>,
            false,
            'active-unread',
          );
          return true;
        });

        setMessages(visibleMessages.slice(0, 10) as SystemMessage[]);
      } catch (err) {
        console.error('Unexpected error fetching messages:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMessages();

    // Set up real-time subscription for new/updated/deleted messages
    const channel = supabase
      .channel('system-messages-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'system_messages'
        },
        (payload) => {
          const newMessage = payload.new as SystemMessageExtended & { is_active?: boolean; target_user_email?: string };
          const nowIso = new Date().toISOString();

          // Never resurrect a message the user has already archived
          if (readIdsRef.current.has(newMessage.id) || dismissedIdsRef.current.has(newMessage.id)) {
            logSystemMessageDecision(
              'realtime-system_messages-insert',
              newMessage.id,
              readIdsRef.current,
              dismissedIdsRef.current,
              true,
              readIdsRef.current.has(newMessage.id) ? 'read' : 'dismissed',
            );
            return;
          }

          const isRelevant = (
            (newMessage.target_type === 'specific_user' && (newMessage.target_user_id === user?.id || (newMessage as any).target_user_email === (user as any)?.email)) ||
            newMessage.target_type === 'all' ||
            (newMessage.target_type === 'all_clients' && userType === 'client') ||
            (newMessage.target_type === 'all_practitioners' && userType === 'practitioner')
          );

          const notExpired = !newMessage.expires_at || newMessage.expires_at > nowIso;
          const active = (newMessage as any).is_active !== false;

          if (!isRelevant || !notExpired || !active) {
            logSystemMessageDecision(
              'realtime-system_messages-insert',
              newMessage.id,
              readIdsRef.current,
              dismissedIdsRef.current,
              true,
              !isRelevant ? 'not-relevant' : !notExpired ? 'expired' : 'inactive',
            );
            return;
          }

          setMessages(current => {
            if (current.some(m => m.id === newMessage.id)) {
              logSystemMessageDecision('realtime-system_messages-insert', newMessage.id, readIdsRef.current, dismissedIdsRef.current, true, 'duplicate');
              return current;
            }
            logSystemMessageDecision('realtime-system_messages-insert', newMessage.id, readIdsRef.current, dismissedIdsRef.current, false, 'active-unread');
            return [newMessage as SystemMessage, ...current].slice(0, 10);
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'system_messages'
        },
        (payload) => {
          const updated = payload.new as SystemMessageExtended & { is_active?: boolean; target_user_email?: string };
          const nowIso = new Date().toISOString();

          // Never resurrect a message the user has already archived (read or dismissed)
          if (readIdsRef.current.has((updated as any).id) || dismissedIdsRef.current.has((updated as any).id)) {
            logSystemMessageDecision(
              'realtime-system_messages-update',
              (updated as any).id,
              readIdsRef.current,
              dismissedIdsRef.current,
              true,
              readIdsRef.current.has((updated as any).id) ? 'read' : 'dismissed',
            );
            setMessages(current => current.filter(m => m.id !== (updated as any).id));
            return;
          }

          const isRelevant = (
            (updated.target_type === 'specific_user' && (updated.target_user_id === user?.id || (updated as any).target_user_email === (user as any)?.email)) ||
            updated.target_type === 'all' ||
            (updated.target_type === 'all_clients' && userType === 'client') ||
            (updated.target_type === 'all_practitioners' && userType === 'practitioner')
          );
          const notExpired = !updated.expires_at || updated.expires_at > nowIso;
          const active = (updated as any).is_active !== false;

          setMessages(current => {
            if (!isRelevant || !notExpired || !active) {
              logSystemMessageDecision(
                'realtime-system_messages-update',
                (updated as any).id,
                readIdsRef.current,
                dismissedIdsRef.current,
                true,
                !isRelevant ? 'not-relevant' : !notExpired ? 'expired' : 'inactive',
              );
              return current.filter(m => m.id !== (updated as any).id);
            }
            const others = current.filter(m => m.id !== (updated as any).id);
            logSystemMessageDecision('realtime-system_messages-update', (updated as any).id, readIdsRef.current, dismissedIdsRef.current, false, 'active-unread');
            return [{ ...(updated as any) }, ...others].slice(0, 10) as SystemMessage[];
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'system_messages'
        },
        (payload) => {
          const deletedId = (payload.old as any)?.id;
          if (!deletedId) return;
          setMessages(current => current.filter(m => m.id !== deletedId));
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'system_message_dismissals',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          const mid = (payload.new as any)?.message_id;
          if (mid) {
            dismissedIdsRef.current.add(mid);
            logSystemMessageDecision('realtime-system_message_dismissals-insert', mid, readIdsRef.current, dismissedIdsRef.current, true, 'dismissed');
            setMessages(current => current.filter(m => m.id !== mid));
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'system_message_reads',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          const mid = (payload.new as any)?.message_id;
          if (mid) {
            readIdsRef.current.add(mid);
            logSystemMessageDecision('realtime-system_message_reads-insert', mid, readIdsRef.current, dismissedIdsRef.current, true, 'read');
            setReadMessages(prev => (prev.includes(mid) ? prev : [...prev, mid]));
            setMessages(current => current.filter(m => m.id !== mid));
          }
        }
      )
      .subscribe();


    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, userType]);

  if (isLoading) {
    return (
      <div className={`bg-muted/30 rounded-md sm:rounded-lg p-4 sm:p-6 w-full ${className}`}>
        <div className="flex items-center space-x-2 mb-4">
          <MessageSquare className="w-5 h-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">{t('dashboard:systemMessages')}</h3>
        </div>
        <div className="space-y-3">
          <div className="h-24 bg-muted rounded animate-pulse" />
          <div className="h-24 bg-muted rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className={`bg-muted/30 rounded-md sm:rounded-lg p-4 sm:p-6 w-full ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <MessageSquare className="w-5 h-5 text-muted-foreground" />
            <h3 className="font-semibold text-foreground">{t('dashboard:systemMessages')}</h3>
          </div>
          <a href="/notifications/history" className="text-sm text-primary hover:underline">
            {t('dashboard:viewHistory', { defaultValue: 'View history' })}
          </a>
        </div>
        <p className="text-muted-foreground text-center py-8">{t('dashboard:noSystemMessages')}</p>
      </div>
    );
  }

  return (
    <div className={`bg-muted/30 rounded-md sm:rounded-lg p-4 sm:p-6 w-full ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <MessageSquare className="w-5 h-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">{t('dashboard:systemMessages')}</h3>
        </div>
        <a href="/notifications/history" className="text-sm text-primary hover:underline">
          {t('dashboard:viewHistory', { defaultValue: 'View history' })}
        </a>
      </div>

      <div className="relative">
        <ScrollArea className="h-80">
          <div className="space-y-3 pr-2 sm:pr-4">
            {messages.map((message) => (
              <SystemMessageCard
                key={message.id}
                id={message.id}
                title={message.title}
                body={message.body}
                createdAt={message.created_at}
                onDismiss={handleDismiss}
                isRead={readMessages.includes(message.id)}
                onMarkAsRead={handleMarkAsRead}
              />
            ))}
          </div>
        </ScrollArea>

        {messages.length > 3 && (
          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-muted/30 to-transparent pointer-events-none" />
        )}
      </div>
    </div>
  );
};

export default SystemMessagesList;